import { MAX_FIND_ALL_HITS, type FindHit } from "./findInOpenedTabs.js";
import { findNextIndex } from "../text/findInText.js";

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function compileGlob(glob: string): RegExp {
  const re = `^${glob.split("*").map((part) => part.split("?").map(escapeRegex).join(".")).join(".*")}$`;
  return new RegExp(re, "i");
}

function parseFilterExpr(filters: string): RegExp[] {
  const raw = filters
    .split(/[;,]/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (!raw.length) return [compileGlob("*")];
  return raw.map((x) => compileGlob(x === "*.*" ? "*" : x));
}

function fileNameMatchesFilters(name: string, filters: RegExp[]): boolean {
  return filters.some((re) => re.test(name));
}

export async function collectHitsInDirectory(
  root: FileSystemDirectoryHandle,
  needle: string,
  caseSensitive: boolean,
  wholeWord: boolean,
  includeSubfolders: boolean,
  filtersExpr: string,
): Promise<FindHit[]> {
  const hits: FindHit[] = [];
  if (!needle.trim()) return hits;
  const filters = parseFilterExpr(filtersExpr);

  const walk = async (dir: FileSystemDirectoryHandle, relPrefix: string): Promise<void> => {
    for await (const [name, handle] of dir.entries()) {
      if (hits.length >= MAX_FIND_ALL_HITS) return;
      const relPath = relPrefix ? `${relPrefix}/${name}` : name;
      if (handle.kind === "directory") {
        if (includeSubfolders) {
          await walk(handle as FileSystemDirectoryHandle, relPath);
        }
        continue;
      }
      if (!fileNameMatchesFilters(name, filters)) continue;
      const fileHandle = handle as FileSystemFileHandle;
      let text = "";
      try {
        const file = await fileHandle.getFile();
        text = await file.text();
      } catch {
        continue;
      }
      let from = 0;
      while (hits.length < MAX_FIND_ALL_HITS) {
        const i = findNextIndex(text, needle, from, caseSensitive, wholeWord);
        if (i < 0) break;
        const line = text.slice(0, i).split(/\r\n|\n|\r/).length;
        const lineStart = Math.max(text.lastIndexOf("\n", i - 1), text.lastIndexOf("\r", i - 1)) + 1;
        let lineEnd = text.indexOf("\n", i);
        const crEnd = text.indexOf("\r", i);
        if (lineEnd < 0 || (crEnd >= 0 && crEnd < lineEnd)) lineEnd = crEnd;
        const preview = text.slice(lineStart, lineEnd < 0 ? text.length : lineEnd).slice(0, 200);
        hits.push({
          rootFolderName: root.name,
          relativePath: relPath,
          fileHandle,
          displayPath: `${root.name}/${relPath}`,
          line,
          offset: i,
          length: needle.length,
          preview,
        });
        from = i + Math.max(1, needle.length);
      }
    }
  };

  await walk(root, "");
  return hits;
}
